import { useState, useEffect } from "react";
import { useSchedulesRidingListQuery } from "src/services/react-query-apis/get-schedules-riding-list";
import * as XLSX from "xlsx";
import { QuerySpinner } from "src/components/common/query-spinner";
import { QueryError } from "src/components/common/query-error";

function exportBusSchedule(
  header,
  tableHeader,
  tableBodyData,
  filename,
  showExtraColumns = false
) {
  // Convert JSX elements to text for Excel export
  const processedTableBodyData = tableBodyData.map(row =>
    row.map(cell => {
      if (typeof cell === "object" && cell !== null && cell.props) {
        // Extract text from JSX element
        return cell.props.children || "";
      }
      return cell;
    })
  );

  const data = [
    [header], // Title row to be merged across columns
    tableHeader,
    ...processedTableBodyData,
  ];

  // Convert to worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Determine column count based on checkbox state
  const columnCount = showExtraColumns ? 8 : 6;

  // Add merge info
  worksheet["!merges"] = [
    // Merge top title across all columns
    { s: { r: 0, c: 0 }, e: { r: 0, c: columnCount - 1 } },
  ];

  // Set column widths (in characters)
  const columnWidths = showExtraColumns
    ? [
        { wch: 20 }, // バス停
        { wch: 20 }, // 名前
        { wch: 20 }, // フリガナ
        { wch: 15 }, // 電話番号
        { wch: 12 }, // 会員番号
        { wch: 5 }, // 往
        { wch: 5 }, // 復
        { wch: 20 }, // 備考
      ]
    : [
        { wch: 20 }, // バス停
        { wch: 20 }, // 名前
        { wch: 20 }, // フリガナ
        { wch: 5 }, // 往
        { wch: 5 }, // 復
        { wch: 20 }, // 備考
      ];

  worksheet["!cols"] = columnWidths;

  // Create and save workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "バス");
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

const ScheduleRidingList = ({ selectedSchedule, selectedMonth, onClose }) => {
  // State for showing/hiding additional columns with localStorage persistence
  const [showExtraColumns, setShowExtraColumns] = useState(() => {
    const saved = localStorage.getItem("ridingListShowExtraColumns");
    return saved ? JSON.parse(saved) : false;
  });

  // Save state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "ridingListShowExtraColumns",
      JSON.stringify(showExtraColumns)
    );
  }, [showExtraColumns]);

  // ===== REAL API DATA SECTION =====
  // Uncomment these lines when ready to use real API data
  const schedulesRidingListQuery = useSchedulesRidingListQuery({
    busScheduleId: selectedSchedule.id,
    month: selectedMonth,
    queryConfig: {
      enabled: !!selectedSchedule.id,
    },
  });

  // Use real API data
  const busSchedule = selectedSchedule;
  const busStopsWithMembers =
    schedulesRidingListQuery.data?.data.bus_course.bus_routes || [];

  // Process the API response to get routes with riders
  const routes = busStopsWithMembers
    .filter(busStop => busStop.members && busStop.members.length > 0)
    .map(busStop => ({
      route: busStop,
      riderAttendances: busStop.members,
    }));

  const getMemberPassengers = (member, prefix) => {
    return member[`${prefix}_passengers`] || 0;
  };

  const routeTotalPassengers = (routeRiderAttendances, prefix) => {
    return routeRiderAttendances.reduce((total, member) => {
      return total + getMemberPassengers(member, prefix);
    }, 0);
  };

  const totalPassengers = prefix => {
    return routes.reduce((total, route) => {
      return total + routeTotalPassengers(route.riderAttendances, prefix);
    }, 0);
  };

  // Create table header based on checkbox state
  // Order: バス停, 会員番号(optional), 名前, フリガナ, 往, 復, 電話番号(optional), 備考
  const tableHeader = showExtraColumns
    ? ["バス停", "会員番号", "名前", "フリガナ", "往", "復", "電話番号", "備考"]
    : ["バス停", "名前", "フリガナ", "往", "復", "備考"];

  const tableBodyData = [];

  routes.forEach(routeData => {
    const busStop = routeData.route;
    const stopName = busStop.stop_name;
    const busStopTime = `${busStop.formatted_outbound_bus_stop_time}発${busStop.formatted_return_bus_stop_time}着`;

    let firstCellContent = {
      0: stopName,
      1: busStopTime,
    };

    routeData.riderAttendances.forEach((member, index, arr) => {
      const comment = member.note || "";

      const outboundPassengers = getMemberPassengers(member, "outbound");
      const returnPassengers = getMemberPassengers(member, "return");

      const passengerString = passengers => {
        if (passengers === 0) return "";
        if (passengers === 1) return "〇";
        return "◎";
      };

      // Create clickable links for student details
      const createStudentLink = (text, memberId) => {
        if (!text || !memberId) return text || "";
        const url = `/admin/student/details/${memberId}?action=view&key=tab1`;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "#007bff" }}
            onClick={e => e.stopPropagation()}
          >
            {text}
          </a>
        );
      };

      if (arr.length === 1) {
        if (showExtraColumns) {
          // Order: バス停, 会員番号, 名前, フリガナ, 往, 復, 電話番号, 備考
          tableBodyData.push([
            firstCellContent[0],
            createStudentLink(member?.member_code, member?.id),
            createStudentLink(member?.name, member?.id),
            member?.furigana || "",
            passengerString(outboundPassengers),
            passengerString(returnPassengers),
            member?.phone_no_1 || "",
            comment,
          ]);

          tableBodyData.push([firstCellContent[1], "", "", "", "", "", "", ""]);
        } else {
          tableBodyData.push([
            firstCellContent[0],
            createStudentLink(member?.name, member?.id),
            member?.furigana || "",
            passengerString(outboundPassengers),
            passengerString(returnPassengers),
            comment,
          ]);

          tableBodyData.push([firstCellContent[1], "", "", "", "", ""]);
        }
      } else {
        if (showExtraColumns) {
          // Order: バス停, 会員番号, 名前, フリガナ, 往, 復, 電話番号, 備考
          tableBodyData.push([
            firstCellContent[index] || "",
            createStudentLink(member?.member_code, member?.id),
            createStudentLink(member?.name, member?.id),
            member?.furigana || "",
            passengerString(outboundPassengers),
            passengerString(returnPassengers),
            member?.phone_no_1 || "",
            comment,
          ]);
        } else {
          tableBodyData.push([
            firstCellContent[index] || "",
            createStudentLink(member?.name, member?.id),
            member?.furigana || "",
            passengerString(outboundPassengers),
            passengerString(returnPassengers),
            comment,
          ]);
        }
      }
    });

    if (showExtraColumns) {
      tableBodyData.push([
        "",
        "",
        "",
        "",
        routeTotalPassengers(routeData.riderAttendances, "outbound"),
        routeTotalPassengers(routeData.riderAttendances, "return"),
        "",
        "",
      ]);

      tableBodyData.push(["", "", "", "", "", "", "", ""]);
    } else {
      tableBodyData.push([
        "",
        "",
        "",
        routeTotalPassengers(routeData.riderAttendances, "outbound"),
        routeTotalPassengers(routeData.riderAttendances, "return"),
        "",
      ]);

      tableBodyData.push(["", "", "", "", "", ""]);
    }
  });

  if (showExtraColumns) {
    tableBodyData.push(["", "", "", "", "往", "復", "", ""]);
    tableBodyData.push([
      "",
      "",
      "",
      "",
      totalPassengers("outbound"),
      totalPassengers("return"),
      "",
      "",
    ]);
  } else {
    tableBodyData.push(["", "", "", "往", "復", ""]);
    tableBodyData.push([
      "",
      "",
      "",
      totalPassengers("outbound"),
      totalPassengers("return"),
      "",
    ]);
  }

  if (schedulesRidingListQuery.isPending) {
    return <QuerySpinner />;
  }

  if (schedulesRidingListQuery.isError) {
    return <QueryError />;
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className="mb-3">
          <div className="form-check" style={{ fontSize: "1.1rem" }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="showExtraColumns"
              checked={showExtraColumns}
              onChange={e => setShowExtraColumns(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showExtraColumns">
              会員番号・電話番号を表示
            </label>
          </div>
        </div>
        <div className="table-responsive" id="ridingTable">
          <div className="p-3 mb-3 light-border">
            <div className="bg-light p-2">
              <div className="row ride_headline">
                <h5 className="">{busSchedule?.description}</h5>
              </div>
            </div>
            <div className="table-responsive">
              <table className="mb-0 table table-bordered mt-2 custom-tbl2 align-middle">
                <thead style={{ marginTop: "10px" }}>
                  <tr>
                    {tableHeader.map((header, index, arr) => (
                      <th
                        key={index}
                        className={`text-center ${
                          index === arr.length - 1 ? "w-25" : ""
                        }`}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ fontSize: "15px" }}>
                  {tableBodyData.length > 0 ? (
                    tableBodyData.map((row, index) => (
                      <tr key={index}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`${
                              showExtraColumns
                                ? [4, 5].includes(cellIndex)
                                  ? "text-center"
                                  : ""
                                : [3, 4].includes(cellIndex)
                                  ? "text-center"
                                  : ""
                            }`}
                          >
                            {cell === "" ? (
                              <div>&nbsp;</div>
                            ) : typeof cell === "object" && cell !== null ? (
                              cell
                            ) : (
                              cell
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={showExtraColumns ? 8 : 6}
                        className="text-center"
                      >
                        乗車予定の生徒がいません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {tableBodyData.length > 0 && (
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-primary"
                onClick={() => {
                  const filename = `${selectedMonth}_${busSchedule?.description}`;
                  const header = `${busSchedule?.description}`;
                  exportBusSchedule(
                    header,
                    tableHeader,
                    tableBodyData,
                    filename,
                    showExtraColumns
                  );
                }}
              >
                ダウンロード
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleRidingList;
